import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { PrinterIcon } from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ReportTextCell, CrewDataTableFrame, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import {
  formatStatNum,
  StatsBucketChart,
  StatsBucketHeader,
  StatsCountCell,
  StatsKpiListLayout,
  StatsMixPanel,
  StatsPageShell,
  StatsRankedBars,
} from './statisticsShared';
import { onboardCountPrintPath } from './reports/onboardCountPrintMeta';
import type { CrewStatMixItem, CrewStatsOnboardRow } from './types';

export const MONTH_BUCKETS = [
  { key: 'm0_3', label: '< 3 mo', test: (m: number) => m < 3 },
  { key: 'm3_6', label: '3–6 mo', test: (m: number) => m >= 3 && m < 6 },
  { key: 'm6_9', label: '6–9 mo', test: (m: number) => m >= 6 && m < 9 },
  { key: 'm9_12', label: '9–12 mo', test: (m: number) => m >= 9 && m < 12 },
  { key: 'm12', label: '≥ 12 mo', test: (m: number) => m >= 12 },
] as const;

/** Fill classes for month buckets — same colors in the chart and table headers. */
const MONTH_BAR: Record<string, string> = {
  m0_3: 'bg-sky-500',
  m3_6: 'bg-blue-500',
  m6_9: 'bg-indigo-500',
  m9_12: 'bg-amber-500',
  m12: 'bg-rose-500',
  unknown: 'bg-slate-400',
};

const MONTH_BAR_BY_LABEL: Record<string, string> = {
  '< 3 mo': MONTH_BAR.m0_3,
  '3–6 mo': MONTH_BAR.m3_6,
  '6–9 mo': MONTH_BAR.m6_9,
  '9–12 mo': MONTH_BAR.m9_12,
  '≥ 12 mo': MONTH_BAR.m12,
  'No sign-on': MONTH_BAR.unknown,
};

type MonthBucketKey = (typeof MONTH_BUCKETS)[number]['key'] | 'unknown';

export type OnboardCountRow = {
  key: string;
  vessel: string;
  nationality: string;
  m0_3: number;
  m3_6: number;
  m6_9: number;
  m9_12: number;
  m12: number;
  unknown: number;
  total: number;
};

function monthBucket(months: number | null | undefined): MonthBucketKey {
  if (months == null || !Number.isFinite(months)) return 'unknown';
  const match = MONTH_BUCKETS.find((b) => b.test(months));
  return match?.key ?? 'unknown';
}

export function buildOnboardCountRows(onboard: CrewStatsOnboardRow[]): OnboardCountRow[] {
  const map = new Map<string, OnboardCountRow>();
  for (const row of onboard) {
    const vessel = row.vessel?.trim() || '—';
    const nationality = row.nationality?.trim() || 'Unknown';
    const key = `${vessel}\0${nationality}`;
    let agg = map.get(key);
    if (!agg) {
      agg = {
        key,
        vessel,
        nationality,
        m0_3: 0,
        m3_6: 0,
        m6_9: 0,
        m9_12: 0,
        m12: 0,
        unknown: 0,
        total: 0,
      };
      map.set(key, agg);
    }
    const bucket = monthBucket(row.monthsOnboard);
    agg[bucket] += 1;
    agg.total += 1;
  }
  return Array.from(map.values()).sort((a, b) => {
    const byVessel = a.vessel.localeCompare(b.vessel, undefined, { sensitivity: 'base' });
    if (byVessel !== 0) return byVessel;
    return a.nationality.localeCompare(b.nationality, undefined, { sensitivity: 'base' });
  });
}

export function monthMixFromCountRows(rows: OnboardCountRow[]): CrewStatMixItem[] {
  const items: CrewStatMixItem[] = MONTH_BUCKETS.map((b) => ({
    label: b.label,
    count: rows.reduce((s, r) => s + r[b.key], 0),
  }));
  const unknown = rows.reduce((s, r) => s + r.unknown, 0);
  if (unknown > 0) items.push({ label: 'No sign-on', count: unknown });
  return items;
}

export function nationalityMixFromCountRows(rows: OnboardCountRow[]): CrewStatMixItem[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    map.set(row.nationality, (map.get(row.nationality) ?? 0) + row.total);
  }
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

const OnboardMixPanel: React.FC<{
  onboardCount: number;
  vesselCount: number;
  nationalityCount: number;
  monthMix: CrewStatMixItem[];
  nationalityMix: CrewStatMixItem[];
}> = ({ onboardCount, vesselCount, nationalityCount, monthMix, nationalityMix }) => {
  const { colors } = useTheme();
  return (
    <StatsMixPanel
      metrics={[
        { label: 'Onboard', value: formatStatNum(onboardCount) },
        { label: 'Vessels', value: formatStatNum(vesselCount) },
        { label: 'Nationalities', value: formatStatNum(nationalityCount) },
      ]}
    >
      <StatsBucketChart
        title="Months onboard"
        hint="Current tour"
        data={monthMix}
        total={onboardCount}
        emptyLabel="No onboard crew"
        barClassFor={(label) => MONTH_BAR_BY_LABEL[label] ?? MONTH_BAR.unknown}
      />
      <div className={cn('shrink-0 border-t', colors.border)} />
      <StatsRankedBars
        title="Nationality mix"
        hint={
          nationalityMix.length
            ? `${nationalityMix.length} ${nationalityMix.length === 1 ? 'nationality' : 'nationalities'}`
            : undefined
        }
        data={nationalityMix}
        total={onboardCount}
        emptyLabel="No onboard crew"
      />
    </StatsMixPanel>
  );
};

const OnboardStatisticsPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const [listTab, setListTab] = useState<'summary' | 'crew'>('summary');

  const summaryColumns: Column<OnboardCountRow>[] = useMemo(() => {
    const monthCols: Column<OnboardCountRow>[] = MONTH_BUCKETS.map((b) => ({
      key: b.key,
      header: <StatsBucketHeader colorClass={MONTH_BAR[b.key]} label={b.label} />,
      sortable: true,
      align: 'right' as const,
      footerType: 'sum' as const,
      columnGroup: { id: 'months', header: 'Months onboard' },
      render: (v: unknown) => <StatsCountCell value={v} emphasize={b.key === 'm12'} />,
    }));
    return [
      {
        key: 'vessel',
        header: 'Vessel',
        sortable: true,
        render: (v: string) => <ReportTextCell value={v} />,
      },
      {
        key: 'nationality',
        header: 'Nationality',
        sortable: true,
        render: (v: string) => <ReportTextCell value={v} />,
      },
      ...monthCols,
      {
        key: 'unknown',
        header: <StatsBucketHeader colorClass={MONTH_BAR.unknown} label="No sign-on" />,
        sortable: true,
        align: 'right',
        footerType: 'sum',
        columnGroup: { id: 'months', header: 'Months onboard' },
        render: (v: unknown) => <StatsCountCell value={v} />,
      },
      {
        key: 'total',
        header: 'Total',
        sortable: true,
        align: 'right',
        footerType: 'sum',
        render: (v: number) => (
          <span className={cn('tabular-nums font-semibold', colors.text)}>{v}</span>
        ),
      },
    ];
  }, [colors.text]);

  const crewColumns: Column<CrewStatsOnboardRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Crew',
        sortable: true,
        render: (_v, row) => (
          <Link
            to={`/crew/${row.crewId}`}
            state={{ from: listReturnPath }}
            className={cn('text-sm hover:underline', colors.primaryFg)}
            onClick={(e) => e.stopPropagation()}
          >
            {row.name}
          </Link>
        ),
      },
      { key: 'vessel', header: 'Vessel', sortable: true, render: (v: string | null) => <ReportTextCell value={v} /> },
      {
        key: 'nationality',
        header: 'Nationality',
        sortable: true,
        render: (v: string | null) => <ReportTextCell value={v} />,
      },
      { key: 'rank', header: 'Rank', sortable: true, render: (v: string | null) => <ReportTextCell value={v} /> },
      {
        key: 'signOnDate',
        header: 'Sign on',
        sortable: true,
        render: (v: string | null) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'monthsOnboard',
        header: 'Months ONB',
        sortable: true,
        align: 'right',
        render: (v: number | null) => {
          const longTour = typeof v === 'number' && v >= 12;
          return (
            <span
              className={cn(
                'tabular-nums',
                longTour ? 'font-medium text-rose-600 dark:text-rose-400' : undefined,
              )}
            >
              <ReportTextCell value={typeof v === 'number' ? formatStatNum(v, v % 1 === 0 ? 0 : 1) : null} />
            </span>
          );
        },
      },
    ],
    [listReturnPath, colors.primaryFg],
  );

  return (
    <StatsPageShell
      hintTitle="Onboard"
      hintBody="Count of currently onboard crew per vessel, nationality, and months onboard (current ONB tour)."
      filterMode="vessel"
      trailingActions={({ applied }) =>
        applied ? (
          <Link
            to={onboardCountPrintPath({ vesselId: applied.vesselId })}
            title="Print preview"
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
              colors.border,
              colors.text,
              'hover:bg-black/5 dark:hover:bg-white/10',
            )}
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </Link>
        ) : null
      }
    >
      {({ data }) => {
        const onboard = data.lists?.onboard ?? [];
        const rows = buildOnboardCountRows(onboard);
        const vesselCount = new Set(rows.map((r) => r.vessel)).size;
        const nationalityCount = new Set(rows.map((r) => r.nationality)).size;
        const showUnknown = rows.some((r) => r.unknown > 0);
        const columns = showUnknown
          ? summaryColumns
          : summaryColumns.filter((c) => c.key !== 'unknown');
        const monthMix = monthMixFromCountRows(rows);
        const nationalityMix = nationalityMixFromCountRows(rows);

        return (
          <StatsKpiListLayout
            charts={
              <OnboardMixPanel
                onboardCount={onboard.length}
                vesselCount={vesselCount}
                nationalityCount={nationalityCount}
                monthMix={monthMix}
                nationalityMix={nationalityMix}
              />
            }
            chartsPlacement="right"
            tabs={[
              { id: 'summary', label: 'Counts', count: rows.length },
              { id: 'crew', label: 'Crew', count: onboard.length },
            ]}
            activeTab={listTab}
            onTabChange={(id) => setListTab(id === 'crew' ? 'crew' : 'summary')}
          >
            <CrewDataTableFrame>
              {listTab === 'summary' ? (
                <DataTable
                  data={rows}
                  columns={columns}
                  keyExtractor={(row) => row.key}
                  emptyMessage="No onboard crew"
                  showFooter={rows.length > 0}
                />
              ) : (
                <DataTable
                  data={onboard}
                  columns={crewColumns}
                  keyExtractor={(row) => row.crewId}
                  emptyMessage="No onboard crew"
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              )}
            </CrewDataTableFrame>
          </StatsKpiListLayout>
        );
      }}
    </StatsPageShell>
  );
};

export default OnboardStatisticsPage;
